import { StyleFunctionProps, theme as baseChakraTheme } from '@chakra-ui/react'

// Touch devices fire mouseenter without a matching mouseleave, so a `:hover` (or
// pointer-driven `:focus`) style sticks after the finger lifts. Everything here
// removes that stickiness under `@media (hover: none)` while leaving hover intact
// on real pointer devices. Style interactive components through these variants
// rather than inline `_hover`, so touch-safety stays in one place.

type ButtonVariant = (props: StyleFunctionProps) => Record<string, any>

// Make a Button variant touch-safe — on touch, hovering changes nothing. Spreads
// the variant's own styles and appends the reset keyed last, so the reset wins the
// cascade on touch. It carries the full style itself rather than letting
// extendTheme compose it onto a base variant, so it also works for custom variants,
// which have no base to compose onto.
export const touchSafeHover =
  (variant: ButtonVariant): ButtonVariant =>
  props => {
    const style = variant(props)
    return { ...style, '@media (hover: none)': { _hover: { bg: style.bg ?? 'transparent' } } }
  }

// Every built-in Button variant, made touch-safe (IconButton inherits these too).
export const touchSafeButtonVariants = Object.fromEntries(
  Object.entries(baseChakraTheme.components.Button.variants ?? {}).map(([name, variant]) =>
    typeof variant === 'function' ? [name, touchSafeHover(variant as ButtonVariant)] : [name, variant],
  ),
)

// MenuItem highlight is `_focus`-driven: Chakra focuses the item under the pointer
// and sets the `--menu-bg` var. On touch that focus never leaves, so the highlight
// sticks — pin the var off (in `_dark` too, since Chakra sets it per color mode).
// Merged into `Menu.baseStyle.item`.
export const touchSafeMenuItem = {
  '@media (hover: none)': {
    _focus: { '--menu-bg': 'transparent', _dark: { '--menu-bg': 'transparent' } },
  },
}
